import { nanoid } from 'nanoid';
import { EVENT_NAMES } from '@/constants';
import type { ActionData, BookPageData, ElementAttrs, ElementConfig } from '@/types';
import { getScaleWH } from '@/utils';
import {
  createEventActionData,
  createTailPageImageActionData,
  createTextActionData
} from './dataTemplate';

/** 尾页素材：放 public，路径与 FontManager 一致，避免 Vite import 写入 /src/assets */
const TAIL_PAGE_BACKGROUND = '/images/tail-page/background-rendered.jpg';
const TAIL_PAGE_MASCOT = '/images/tail-page/mascot.png';
const TAIL_PAGE_QR_CARD = '/images/tail-page/qr-card.png';
const TAIL_PAGE_QR_CODE = '/images/tail-page/qr-code.png';

const PAGE_DURATION = 0.1;

type TailPageRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type TailPageRole = NonNullable<ElementConfig['tailPageRole']>;
type TailPageImageRole = Extract<
  TailPageRole,
  'asset-background' | 'asset-mascot' | 'asset-qrCard' | 'asset-qrCode'
>;
type TailPageTextRole = Extract<TailPageRole, 'title' | 'theEnd' | 'subtitle' | 'qrText'>;
type TailPageAlignment = 'edges' | 'center' | 'centerX';

/** 付费版简洁尾页使用的角色 */
type TailPagePaidRole = Exclude<
  TailPageRole,
  'subtitle' | 'qrText' | 'asset-qrCard' | 'asset-qrCode'
>;

/** 免费版二维码尾页使用的角色 */
type TailPageQrRole = Exclude<TailPageRole, 'theEnd'>;

/** 付费版纵向堆叠的角色（mascot → title → theEnd → replayButton） */
type TailPagePaidStackRole = 'asset-mascot' | 'title' | 'theEnd' | 'replayButton';

/** 素材固有宽高比，与 ImageElement._refreshSize 的 getScaleWH 结果一致，用于预写居中 left/top */
const ASSET_ASPECT_RATIO: Record<TailPageImageRole, number> = {
  'asset-background': 1352 / 1502,
  'asset-mascot': 1,
  'asset-qrCard': 170 / 199,
  'asset-qrCode': 1
};

type TailPageTextData = {
  narration: string;
  color: string;
  fontFamily: string;
  fontSizeRatio: number;
  fontWeight: '400' | '500' | '600' | '700';
};

/** 尾页 1x 设计稿高度：空隙与元素高度按设计稿像素换算（与下方 dropShadow 的 /751 基准一致） */
const DESIGN_HEIGHT = 751;

/** 付费版纵向堆叠的元素高度（1x 设计稿 px，与 PAID_LAYOUT.height × DESIGN_HEIGHT 对应） */
const PAID_STACK_ITEM_HEIGHT: Record<TailPagePaidStackRole, number> = {
  'asset-mascot': 55,
  title: 46,
  theEnd: 29,
  replayButton: 52
};

/** 付费版相邻元素空隙（1x 设计稿 px）：上面的元素（mascot/title/theEnd）之间为 30，replayButton 与 theEnd 之间为 45 */
const PAID_STACK_ITEM_GAP: Record<TailPagePaidStackRole, number> = {
  'asset-mascot': 30,
  title: 30,
  theEnd: 45,
  replayButton: 0
};

/** 将整个堆叠组在页面上垂直居中，返回各元素的 top（0-1 比例） */
function resolvePaidStackTop(): Record<TailPagePaidStackRole, number> {
  const order: TailPagePaidStackRole[] = ['asset-mascot', 'title', 'theEnd', 'replayButton'];
  const groupHeight = order.reduce(
    (total, role) => total + PAID_STACK_ITEM_HEIGHT[role] + PAID_STACK_ITEM_GAP[role],
    0
  );

  let top = (DESIGN_HEIGHT - groupHeight) / 2;
  const topRatio = {} as Record<TailPagePaidStackRole, number>;
  order.forEach((role) => {
    topRatio[role] = top / DESIGN_HEIGHT;
    top += PAID_STACK_ITEM_HEIGHT[role] + PAID_STACK_ITEM_GAP[role];
  });
  return topRatio;
}

const PAID_STACK_TOP = resolvePaidStackTop();

/** 付费版尾页布局：mascot / title / THE END / replayButton */
const PAID_LAYOUT = {
  'asset-background': { left: 0, top: 0, width: 1, height: 1 },
  'asset-mascot': {
    left: 0.45931953,
    top: PAID_STACK_TOP['asset-mascot'],
    width: 0.08136095,
    height: 0.07323569
  },
  title: {
    left: 0.32840237,
    top: PAID_STACK_TOP.title,
    width: 0.34319527,
    height: 0.06125166
  },
  theEnd: {
    left: 0.35,
    top: PAID_STACK_TOP.theEnd,
    width: 0.3,
    height: 0.03861518
  },
  replayButton: {
    left: 0.36538462,
    top: PAID_STACK_TOP.replayButton,
    width: 0.26923077,
    height: 0.06924101
  }
} satisfies Record<TailPagePaidRole, TailPageRect>;

/** 免费版二维码尾页：恢复“去除二维码”前的布局与文案 */
const QR_LAYOUT = {
  'asset-background': { left: 0, top: 0, width: 1, height: 1 },
  'asset-mascot': {
    left: 0.45931953,
    top: 0.19241012,
    width: 0.08136095,
    height: 0.07323569
  },
  title: {
    left: 0.32840237,
    top: 0.28695073,
    width: 0.34319527,
    height: 0.06125166
  },
  subtitle: {
    left: 0.31508876,
    top: 0.35885486,
    width: 0.36982249,
    height: 0.03861518
  },
  'asset-qrCard': {
    left: 0.37426036,
    top: 0.42942743,
    width: 0.25147929,
    height: 0.26498003
  },
  'asset-qrCode': {
    left: 0.4112426,
    top: 0.4587217,
    width: 0.17751479,
    height: 0.15978695
  },
  qrText: {
    left: 0.42159763,
    top: 0.62516644,
    width: 0.15680473,
    height: 0.03462051
  },
  replayButton: {
    left: 0.36538462,
    top: 0.73834887,
    width: 0.26923077,
    height: 0.06924101
  }
} satisfies Record<TailPageQrRole, TailPageRect>;

const PAID_TEXT_DATA: Record<'title' | 'theEnd', TailPageTextData> = {
  title: {
    narration: '这个故事结束啦!',
    color: '#663C1B',
    fontFamily: 'SourceHanSansCN',
    fontSizeRatio: 0.04733728,
    fontWeight: '700'
  },
  theEnd: {
    narration: 'THE END',
    color: '#8C6638',
    fontFamily: 'SourceHanSansCN',
    fontSizeRatio: 0.0295858,
    fontWeight: '400'
  }
};

const QR_TEXT_DATA: Record<'title' | 'subtitle' | 'qrText', TailPageTextData> = {
  title: {
    narration: '这个故事结束啦!',
    color: '#663C1B',
    fontFamily: 'SourceHanSansCN',
    fontSizeRatio: 0.04733728,
    fontWeight: '700'
  },
  subtitle: {
    narration: '想制作属于自己的故事绘本?',
    color: '#8C6638',
    fontFamily: 'SourceHanSansCN',
    fontSizeRatio: 0.0295858,
    fontWeight: '400'
  },
  qrText: {
    narration: '扫码一键创作',
    color: '#383B55',
    fontFamily: 'SourceHanSansCN',
    fontSizeRatio: 0.0295858,
    fontWeight: '400'
  }
};

export class KieStoryTailPageDataBuilder {
  private readonly pageWidth: number;
  private readonly pageHeight: number;
  /** true=免费用户，尾页带二维码；false=付费用户，尾页保持简洁布局 */
  private readonly showQrCode: boolean;

  constructor(pageWidth: number, pageHeight: number, showQrCode: boolean) {
    this.pageWidth = pageWidth;
    this.pageHeight = pageHeight;
    this.showQrCode = showQrCode;
  }

  public shouldAppend(pageCount: number): boolean {
    return pageCount % 2 === 1;
  }

  public build(pageIndex: number): BookPageData {
    const actions = this.createActions();
    actions.forEach((action) => {
      action.duration = PAGE_DURATION;
    });

    return {
      id: `page-${nanoid(6)}`,
      needSpineShadow: true,
      side: pageIndex % 2 === 1 ? 'left' : 'right',
      type: 'backCover',
      index: pageIndex,
      originIndex: pageIndex,
      width: this.pageWidth,
      height: this.pageHeight,
      originWidth: this.pageWidth,
      originHeight: this.pageHeight,
      startTime: 0,
      duration: PAGE_DURATION,
      tts: null,
      actions
    };
  }

  private createActions(): ActionData[] {
    return this.showQrCode ? this.createQrCodeActions() : this.createPaidActions();
  }

  /** 付费版尾页：mascot / title / THE END / replayButton */
  private createPaidActions(): ActionData[] {
    return [
      this.createImageAction(
        TAIL_PAGE_BACKGROUND,
        'asset-background',
        0,
        PAID_LAYOUT['asset-background'],
        {
          fullscreen: true
        }
      ),
      this.createImageAction(TAIL_PAGE_MASCOT, 'asset-mascot', 1, PAID_LAYOUT['asset-mascot']),
      this.createTextAction('title', 2, 'centerX', PAID_LAYOUT.title, PAID_TEXT_DATA.title),
      this.createTextAction('theEnd', 3, 'centerX', PAID_LAYOUT.theEnd, PAID_TEXT_DATA.theEnd),
      this.createReplayButton(PAID_LAYOUT.replayButton, 4)
    ];
  }

  /** 免费版尾页：恢复“去除二维码”前的二维码布局 */
  private createQrCodeActions(): ActionData[] {
    return [
      this.createImageAction(
        TAIL_PAGE_BACKGROUND,
        'asset-background',
        0,
        QR_LAYOUT['asset-background'],
        {
          fullscreen: true
        }
      ),
      this.createImageAction(TAIL_PAGE_QR_CARD, 'asset-qrCard', 1, QR_LAYOUT['asset-qrCard']),
      this.createImageAction(TAIL_PAGE_MASCOT, 'asset-mascot', 2, QR_LAYOUT['asset-mascot']),
      this.createImageAction(TAIL_PAGE_QR_CODE, 'asset-qrCode', 3, QR_LAYOUT['asset-qrCode']),
      this.createTextAction('title', 4, 'centerX', QR_LAYOUT.title, QR_TEXT_DATA.title),
      this.createTextAction('subtitle', 5, 'centerX', QR_LAYOUT.subtitle, QR_TEXT_DATA.subtitle),
      this.createTextAction('qrText', 6, 'centerX', QR_LAYOUT.qrText, QR_TEXT_DATA.qrText),
      this.createReplayButton(QR_LAYOUT.replayButton, 7)
    ];
  }

  private createReplayButton(rect: TailPageRect, zIndex: number): ActionData {
    return createEventActionData(EVENT_NAMES.BOOK_REPLAY, this.createElementAttrs(rect, zIndex), {
      tailPageRole: 'replayButton',
      backgroundColor: '#A050FF',
      borderRadius: (66 / 676) * this.pageWidth,
      dropShadow: {
        color: '#A050FF',
        alpha: 0.3,
        blur: (4 / 676) * this.pageWidth,
        offsetX: 0,
        offsetY: (6 / 751) * this.pageHeight
      }
    });
  }

  /** 步骤1：按素材宽高比预缩放；步骤2：以设计中心回写 left/top，兼容 ImageElement.applyParentLayout */
  private createImageAction(
    url: string,
    role: TailPageImageRole,
    zIndex: number,
    rect: TailPageRect,
    config?: ElementConfig
  ): ActionData {
    return createTailPageImageActionData(
      url,
      role,
      this.createImageElementAttrs(role, zIndex, Boolean(config?.fullscreen), rect),
      config
    );
  }

  private createTextAction(
    role: TailPageTextRole,
    zIndex: number,
    alignment: TailPageAlignment,
    rect: TailPageRect,
    textData: TailPageTextData
  ): ActionData {
    // 字号取整，与旁白布局一致，避免 Canvas 小数 px 描边发糊
    const fontSize = Math.max(1, Math.round(textData.fontSizeRatio * this.pageWidth));
    const action = createTextActionData(
      textData.narration,
      {
        fill: { fill: textData.color },
        font: {
          fontFamily: textData.fontFamily,
          fontSize,
          fontWeight: textData.fontWeight
        },
        stroke: { color: textData.color, width: 0, alpha: 0 },
        layout: { align: 'center' },
        multiline: {
          wordWrap: false,
          breakWords: false,
          whiteSpace: 'normal'
        }
      },
      this.createElementAttrs(rect, zIndex, alignment)
    );
    action.element!.config = { tailPageRole: role };
    return action;
  }

  /**
   * 预计算等比缩放后的居中 layout，等价于原 ImageElement 尾页分支：
   * left = centerX - fittedWidthRatio / 2
   * top  = centerY - fittedHeightRatio / 2
   */
  private createImageElementAttrs(
    role: TailPageImageRole,
    zIndex: number,
    fullscreen: boolean,
    rect: TailPageRect
  ): ElementAttrs {
    const parentWidth = this.pageWidth;
    const parentHeight = this.pageHeight;
    const regionW = rect.width * parentWidth;
    const regionH = rect.height * parentHeight;
    const { width, height } = getScaleWH(regionW, regionH, ASSET_ASPECT_RATIO[role], fullscreen);

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    return {
      x: -parentWidth / 2 + centerX * parentWidth,
      y: -parentHeight / 2 + centerY * parentHeight,
      width,
      height,
      zIndex,
      layout: {
        parentWidth,
        parentHeight,
        width: rect.width,
        height: rect.height,
        x: centerX,
        y: centerY,
        left: centerX - width / parentWidth / 2,
        top: centerY - height / parentHeight / 2
      }
    };
  }

  private createElementAttrs(
    rect: TailPageRect,
    zIndex: number,
    alignment: TailPageAlignment = 'edges'
  ) {
    const elementWidth = rect.width * this.pageWidth;
    const elementHeight = rect.height * this.pageHeight;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    return {
      x: -this.pageWidth / 2 + centerX * this.pageWidth,
      y: -this.pageHeight / 2 + centerY * this.pageHeight,
      width: elementWidth,
      height: elementHeight,
      zIndex,
      layout: {
        parentWidth: this.pageWidth,
        parentHeight: this.pageHeight,
        width: rect.width,
        height: rect.height,
        x: centerX,
        y: centerY,
        ...(alignment === 'edges'
          ? { left: rect.left, top: rect.top }
          : alignment === 'centerX'
            ? { top: rect.top }
            : {})
      }
    };
  }
}
